import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { gql, useMutation, useQuery } from "@apollo/client";
import Header from "components/Header";
import { getGraphQLErrorMessage } from "utils/graphqlError";
import { userClient } from "state/apolloClient";
import {
  CONFIRM_MEMBER_TRANSFER_CONTRIBUTION,
  CREATE_FUNDS_DRIVE,
  CREATE_MEMBER_TRANSFER_CONTRIBUTION,
  GET_FUNDS_DRIVES,
  GET_MY_INCOMING_CONTRIBUTIONS,
  GET_MY_OUTGOING_CONTRIBUTIONS,
  UPDATE_FUNDS_DRIVE_STATUS,
} from "graphql/fundsDrives";

const GET_USERS = gql`
  query {
    users(user: {}) {
      id
      firstName
      lastName
      isActive
    }
  }
`;

const formatDate = (value) => (value ? new Date(value).toLocaleString() : "-");
const formatKes = (value) => `KES ${(Number(value) || 0).toLocaleString()}`;

const FundsDrives = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const [tab, setTab] = useState(0);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [onlyUnconfirmed, setOnlyUnconfirmed] = useState(true);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const [createForm, setCreateForm] = useState({
    title: "",
    description: "",
    recipientUserId: "",
    startDate: "",
    endDate: "",
  });

  const [contributionForm, setContributionForm] = useState({
    fundsDriveId: "",
    recipientUserId: "",
    confirmationMessage: "",
  });

  const {
    data: drivesData,
    loading: drivesLoading,
    error: drivesError,
    refetch: refetchDrives,
  } = useQuery(GET_FUNDS_DRIVES, {
    variables: { includeInactive },
    fetchPolicy: "cache-and-network",
  });

  const {
    data: usersData,
    loading: usersLoading,
    error: usersError,
  } = useQuery(GET_USERS, {
    client: userClient,
    fetchPolicy: "cache-and-network",
  });

  const {
    data: incomingData,
    loading: incomingLoading,
    error: incomingError,
    refetch: refetchIncoming,
  } = useQuery(GET_MY_INCOMING_CONTRIBUTIONS, {
    variables: { onlyUnconfirmed },
    fetchPolicy: "cache-and-network",
  });

  const {
    data: outgoingData,
    loading: outgoingLoading,
    error: outgoingError,
    refetch: refetchOutgoing,
  } = useQuery(GET_MY_OUTGOING_CONTRIBUTIONS, {
    fetchPolicy: "cache-and-network",
  });

  const [createDrive, { loading: creatingDrive }] = useMutation(CREATE_FUNDS_DRIVE);
  const [updateDriveStatus, { loading: togglingStatus }] = useMutation(UPDATE_FUNDS_DRIVE_STATUS);
  const [createContribution, { loading: creatingContribution }] = useMutation(
    CREATE_MEMBER_TRANSFER_CONTRIBUTION
  );
  const [confirmIncoming, { loading: confirmingIncoming }] = useMutation(
    CONFIRM_MEMBER_TRANSFER_CONTRIBUTION
  );

  const drives = useMemo(() => drivesData?.fundsDrives || [], [drivesData]);
  const users = useMemo(() => {
    const rows = usersData?.users || [];
    return rows
      .filter((u) => u?.isActive !== false)
      .map((u) => ({
        id: Number(u.id),
        label: `${u.firstName || ""} ${u.lastName || ""}`.trim() || `Member #${u.id}`,
      }));
  }, [usersData]);

  const driveMap = useMemo(() => {
    const map = new Map();
    drives.forEach((d) => map.set(Number(d.id), d));
    return map;
  }, [drives]);

  const incomingRows = useMemo(() => incomingData?.myIncomingMemberTransferContributions || [], [incomingData]);
  const outgoingRows = useMemo(() => outgoingData?.myOutgoingMemberTransferContributions || [], [outgoingData]);

  const showToast = (message, severity = "success") => {
    setToast({ open: true, severity, message });
  };

  const toIsoDateTimeOrNull = (dateValue) => {
    if (!dateValue) return null;
    if (typeof dateValue === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      return `${dateValue}T00:00:00Z`;
    }
    return dateValue;
  };

  const onCreateDrive = async () => {
    if (!createForm.title.trim()) {
      showToast("Drive title is required.", "error");
      return;
    }
    if (!createForm.recipientUserId) {
      showToast("Please choose a recipient member.", "error");
      return;
    }

    try {
      const payload = {
        title: createForm.title.trim(),
        description: createForm.description?.trim() || null,
        recipientUserId: Number(createForm.recipientUserId),
        startDate: toIsoDateTimeOrNull(createForm.startDate),
        endDate: toIsoDateTimeOrNull(createForm.endDate),
      };

      const { data } = await createDrive({ variables: { input: payload } });
      const result = data?.createFundsDrive;
      if (result?.isSuccess) {
        showToast("Funds drive created successfully.");
        setCreateForm({ title: "", description: "", recipientUserId: "", startDate: "", endDate: "" });
        await refetchDrives();
      } else {
        showToast(result?.errorMessage || "Failed to create funds drive.", "error");
      }
    } catch (error) {
      showToast(getGraphQLErrorMessage(error), "error");
    }
  };

  const onToggleDriveStatus = async (drive) => {
    try {
      const { data } = await updateDriveStatus({
        variables: { input: { fundsDriveId: Number(drive.id), isActive: !drive.isActive } },
      });
      const result = data?.updateFundsDriveStatus;
      if (result?.isSuccess) {
        showToast(`Drive marked as ${drive.isActive ? "inactive" : "active"}.`);
        await refetchDrives();
      } else {
        showToast(result?.errorMessage || "Failed to update drive status.", "error");
      }
    } catch (error) {
      showToast(getGraphQLErrorMessage(error), "error");
    }
  };

  const selectedDrive = driveMap.get(Number(contributionForm.fundsDriveId));

  const onSelectDrive = (value) => {
    const drive = driveMap.get(Number(value));
    setContributionForm((prev) => ({
      ...prev,
      fundsDriveId: value,
      recipientUserId: drive?.recipientUserId ? String(drive.recipientUserId) : "",
    }));
  };

  const onSubmitContribution = async () => {
    if (!contributionForm.fundsDriveId) {
      showToast("Select a funds drive before submitting.", "error");
      return;
    }
    if (!contributionForm.recipientUserId) {
      showToast("Recipient was not resolved from selected drive.", "error");
      return;
    }
    if (!contributionForm.confirmationMessage.trim()) {
      showToast("Paste the transaction confirmation message.", "error");
      return;
    }

    try {
      const { data } = await createContribution({
        variables: {
          input: {
            fundsDriveId: Number(contributionForm.fundsDriveId),
            recipientUserId: Number(contributionForm.recipientUserId),
            confirmationMessage: contributionForm.confirmationMessage.trim(),
          },
        },
      });

      const result = data?.createMemberTransferContribution;
      if (result?.isSuccess) {
        showToast("Contribution submitted. Waiting recipient confirmation.");
        setContributionForm({ fundsDriveId: "", recipientUserId: "", confirmationMessage: "" });
        await Promise.all([refetchDrives(), refetchOutgoing(), refetchIncoming()]);
      } else {
        const msg = result?.errorMessage || "Contribution submission failed.";
        const duplicate = /duplicate|reference|already exists|already used/i.test(msg);
        showToast(
          duplicate
            ? `Duplicate transaction reference detected. Please verify and submit a different message/reference. (${msg})`
            : msg,
          "error"
        );
      }
    } catch (error) {
      const msg = getGraphQLErrorMessage(error);
      const duplicate = /duplicate|reference|already exists|already used/i.test(msg);
      showToast(
        duplicate
          ? `Duplicate transaction reference detected. Please verify and submit a different message/reference. (${msg})`
          : msg,
        "error"
      );
    }
  };

  const onConfirmIncoming = async (row, recipientConfirmed = true) => {
    try {
      const { data } = await confirmIncoming({
        variables: {
          input: {
            contributionId: Number(row.id),
            recipientConfirmed,
          },
        },
      });
      const result = data?.confirmMemberTransferContribution;
      if (result?.isSuccess) {
        showToast("Incoming transfer confirmation updated.");
        await Promise.all([refetchIncoming(), refetchDrives()]);
      } else {
        showToast(result?.errorMessage || "Failed to confirm incoming transfer.", "error");
      }
    } catch (error) {
      showToast(getGraphQLErrorMessage(error), "error");
    }
  };

  const driveColumns = [
    { field: "title", headerName: "Drive", flex: 1.2 },
    { field: "recipientName", headerName: "Recipient", flex: 0.9 },
    {
      field: "isActive",
      headerName: "Status",
      flex: 0.7,
      renderCell: ({ row }) => (
        <Chip
          size="small"
          label={row.isActive ? "Active" : "Inactive"}
          color={row.isActive ? "success" : "default"}
          variant={row.isActive ? "filled" : "outlined"}
        />
      ),
    },
    {
      field: "totalContributedAmount",
      headerName: "Total",
      flex: 0.7,
      valueFormatter: ({ value }) => formatKes(value),
    },
    { field: "totalContributionCount", headerName: "Count", flex: 0.5 },
    {
      field: "createdDate",
      headerName: "Created",
      flex: 0.9,
      valueFormatter: ({ value }) => formatDate(value),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1.25,
      sortable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => navigate(`/funds-drives/${row.id}`)}
          >
            View
          </Button>
          <Tooltip title="Toggle active/inactive status">
            <span>
              <Button
                size="small"
                variant="contained"
                color={row.isActive ? "warning" : "success"}
                disabled={togglingStatus}
                onClick={() => onToggleDriveStatus(row)}
              >
                {row.isActive ? "Deactivate" : "Activate"}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  const contributionColumns = [
    { field: "fundsDriveTitle", headerName: "Drive", flex: 1.1 },
    { field: "recipientName", headerName: "Recipient", flex: 0.85 },
    { field: "amount", headerName: "Amount", flex: 0.65, valueFormatter: ({ value }) => formatKes(value) },
    { field: "transactionReference", headerName: "Reference", flex: 1 },
    {
      field: "transactionDateTime",
      headerName: "Transaction Date",
      flex: 1,
      valueFormatter: ({ value }) => formatDate(value),
    },
    {
      field: "recipientConfirmed",
      headerName: "Confirmation",
      flex: 0.8,
      renderCell: ({ value }) => (
        <Chip
          size="small"
          color={value ? "success" : "warning"}
          label={value ? "Confirmed" : "Pending"}
        />
      ),
    },
  ];

  const incomingColumns = [
    { field: "fundsDriveTitle", headerName: "Drive", flex: 1.05 },
    { field: "senderName", headerName: "Sender", flex: 0.8 },
    { field: "amount", headerName: "Amount", flex: 0.65, valueFormatter: ({ value }) => formatKes(value) },
    { field: "transactionReference", headerName: "Reference", flex: 0.9 },
    {
      field: "createdDate",
      headerName: "Submitted",
      flex: 0.9,
      valueFormatter: ({ value }) => formatDate(value),
    },
    {
      field: "actions",
      headerName: "Action",
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => (
        <Button
          size="small"
          variant="contained"
          disabled={confirmingIncoming || row.recipientConfirmed}
          onClick={() => onConfirmIncoming(row, true)}
        >
          {row.recipientConfirmed ? "Confirmed" : "Confirm Receipt"}
        </Button>
      ),
    },
  ];

  return (
    <Box m={{ xs: "1rem", sm: "1.5rem 2.5rem" }} overflow="hidden">
      <Header
        title="FUNDS DRIVES"
        subtitle="Manage member-to-member fundraisers, submit direct contributions, and confirm incoming transfers."
      />

      <Card sx={{ mt: 2, borderRadius: 3, border: `1px solid ${theme.palette.divider}`, overflow: "hidden" }}>
        <CardContent>
          <Tabs
            value={tab}
            onChange={(_, value) => setTab(value)}
            variant={isMobile ? "scrollable" : "standard"}
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              mb: 2,
              "& .MuiTab-root": { whiteSpace: "nowrap", minHeight: 44, px: { xs: 1.5, sm: 2 } },
            }}
          >
            <Tab label="Drives" />
            <Tab label="Submit Contribution" />
            <Tab label="Incoming Confirmations" />
            <Tab label="My Outgoing" />
          </Tabs>

          {tab === 0 && (
            <Grid container spacing={2} direction={{ xs: "column-reverse", md: "row" }}>
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ borderRadius: 3 }}>
                  <CardContent>
                    <Typography variant="h5" fontWeight={700} mb={1}>
                      Create Funds Drive
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      Create a named fundraiser so members can submit contributions against the right purpose.
                    </Typography>
                    <Stack spacing={1.5}>
                      <TextField
                        fullWidth
                        label="Drive title"
                        value={createForm.title}
                        onChange={(e) => setCreateForm((p) => ({ ...p, title: e.target.value }))}
                      />
                      <TextField
                        fullWidth
                        label="Description"
                        multiline
                        minRows={3}
                        value={createForm.description}
                        onChange={(e) => setCreateForm((p) => ({ ...p, description: e.target.value }))}
                      />
                      <TextField
                        fullWidth
                        select
                        label="Recipient member"
                        value={createForm.recipientUserId}
                        onChange={(e) => setCreateForm((p) => ({ ...p, recipientUserId: e.target.value }))}
                        helperText="Contribution recipient for this drive"
                      >
                        {users.map((user) => (
                          <MenuItem key={user.id} value={String(user.id)}>
                            {user.label}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        fullWidth
                        label="Start date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={createForm.startDate}
                        onChange={(e) => setCreateForm((p) => ({ ...p, startDate: e.target.value }))}
                      />
                      <TextField
                        fullWidth
                        label="End date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={createForm.endDate}
                        onChange={(e) => setCreateForm((p) => ({ ...p, endDate: e.target.value }))}
                      />
                      <Button
                        variant="contained"
                        fullWidth={isMobile}
                        onClick={onCreateDrive}
                        disabled={creatingDrive || usersLoading}
                      >
                        {creatingDrive ? "Creating..." : "Create Drive"}
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={8}>
                <Card variant="outlined" sx={{ borderRadius: 3 }}>
                  <CardContent>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      justifyContent="space-between"
                      spacing={1}
                      mb={1.5}
                    >
                      <Typography variant="h5" fontWeight={700}>
                        Funds Drives
                      </Typography>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={includeInactive}
                            onChange={(e) => setIncludeInactive(e.target.checked)}
                          />
                        }
                        label="Show inactive"
                      />
                    </Stack>

                    {(drivesLoading || usersLoading) && (
                      <Stack alignItems="center" py={5}>
                        <CircularProgress size={28} />
                      </Stack>
                    )}

                    {(drivesError || usersError) && (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        {getGraphQLErrorMessage(drivesError || usersError)}
                      </Alert>
                    )}

                    {!drivesLoading && !drivesError && drives.length === 0 && (
                      <Alert severity="info">No funds drives found. Create one to get started.</Alert>
                    )}

                    {!drivesLoading && !drivesError && drives.length > 0 && (
                      <>
                        {isMobile ? (
                          <Stack spacing={1.25}>
                            {drives.map((row) => (
                              <Card key={row.id} variant="outlined" sx={{ borderRadius: 2 }}>
                                <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                                  <Stack spacing={1}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                                      <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                                        {row.title}
                                      </Typography>
                                      <Chip
                                        size="small"
                                        label={row.isActive ? "Active" : "Inactive"}
                                        color={row.isActive ? "success" : "default"}
                                        variant={row.isActive ? "filled" : "outlined"}
                                      />
                                    </Stack>
                                    <Typography variant="body2" color="text.secondary">
                                      Recipient: {row.recipientName || "-"}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      Total: {formatKes(row.totalContributedAmount)}
                                    </Typography>
                                    <Stack direction="row" spacing={1}>
                                      <Button size="small" variant="outlined" onClick={() => navigate(`/funds-drives/${row.id}`)}>
                                        View
                                      </Button>
                                      <Button
                                        size="small"
                                        variant="contained"
                                        color={row.isActive ? "warning" : "success"}
                                        disabled={togglingStatus}
                                        onClick={() => onToggleDriveStatus(row)}
                                      >
                                        {row.isActive ? "Deactivate" : "Activate"}
                                      </Button>
                                    </Stack>
                                  </Stack>
                                </CardContent>
                              </Card>
                            ))}
                          </Stack>
                        ) : (
                          <Box height="62vh">
                            <DataGrid
                              rows={drives}
                              columns={driveColumns}
                              getRowId={(row) => row.id}
                              disableRowSelectionOnClick
                              pageSizeOptions={[10, 20, 50]}
                              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                            />
                          </Box>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {tab === 1 && (
            <Card variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h5" fontWeight={700} mb={1}>
                  Submit Contribution
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Use the same confirmation-message workflow used in account transaction upload. Select a drive and paste the full SMS/bank message.
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={5}>
                    <TextField
                      fullWidth
                      select
                      label="Funds drive"
                      value={contributionForm.fundsDriveId}
                      onChange={(e) => onSelectDrive(e.target.value)}
                    >
                      {drives
                        .filter((d) => d.isActive)
                        .map((drive) => (
                          <MenuItem key={drive.id} value={String(drive.id)}>
                            {drive.title} • {drive.recipientName}
                          </MenuItem>
                        ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Recipient"
                      value={selectedDrive?.recipientName || ""}
                      helperText="Auto-bound from selected drive"
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Button
                      fullWidth
                      sx={{ height: "100%" }}
                      variant="outlined"
                      component={selectedDrive ? RouterLink : "button"}
                      to={selectedDrive ? `/funds-drives/${selectedDrive.id}` : undefined}
                      disabled={!selectedDrive}
                    >
                      View Drive
                    </Button>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={6}
                      label="Transaction confirmation message"
                      placeholder="Paste the full bank/SMS confirmation message exactly as received..."
                      helperText="Tip: include complete reference details so Centra can parse amount, date, and reference consistently."
                      value={contributionForm.confirmationMessage}
                      onChange={(e) =>
                        setContributionForm((prev) => ({ ...prev, confirmationMessage: e.target.value }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      onClick={onSubmitContribution}
                      disabled={creatingContribution}
                    >
                      {creatingContribution ? "Submitting..." : "Submit Contribution"}
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}

          {tab === 2 && (
            <Card variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                  <Typography variant="h5" fontWeight={700}>
                    Incoming Confirmations
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={onlyUnconfirmed}
                        onChange={(e) => setOnlyUnconfirmed(e.target.checked)}
                      />
                    }
                    label="Only unconfirmed"
                  />
                </Stack>

                {incomingLoading && (
                  <Stack alignItems="center" py={4}>
                    <CircularProgress size={28} />
                  </Stack>
                )}
                {incomingError && <Alert severity="error">{getGraphQLErrorMessage(incomingError)}</Alert>}
                {!incomingLoading && !incomingError && incomingRows.length === 0 && (
                  <Alert severity="info">No incoming contributions found for this filter.</Alert>
                )}

                {!incomingLoading && !incomingError && incomingRows.length > 0 && (
                  <Box height="60vh">
                    <DataGrid
                      rows={incomingRows}
                      columns={incomingColumns}
                      getRowId={(row) => row.id}
                      disableRowSelectionOnClick
                      pageSizeOptions={[10, 20, 50]}
                      initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {tab === 3 && (
            <Card variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h5" fontWeight={700} mb={2}>
                  My Outgoing Contributions
                </Typography>
                {outgoingLoading && (
                  <Stack alignItems="center" py={4}>
                    <CircularProgress size={28} />
                  </Stack>
                )}
                {outgoingError && <Alert severity="error">{getGraphQLErrorMessage(outgoingError)}</Alert>}
                {!outgoingLoading && !outgoingError && outgoingRows.length === 0 && (
                  <Alert severity="info">You have not submitted any contribution yet.</Alert>
                )}
                {!outgoingLoading && !outgoingError && outgoingRows.length > 0 && (
                  <Box height="60vh">
                    <DataGrid
                      rows={outgoingRows}
                      columns={contributionColumns}
                      getRowId={(row) => row.id}
                      disableRowSelectionOnClick
                      pageSizeOptions={[10, 20, 50]}
                      initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          <Divider sx={{ mt: 2 }} />
        </CardContent>
      </Card>

      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
      >
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FundsDrives;
